import { Icon } from "@platform/ui/components";
import { focusRingStyles } from "@platform/ui/styles";
import { useQuery } from "@tanstack/react-query";
import * as fuzzy from "fuzzy";
import * as React from "react";
import { useNavigate } from "react-router";
import { DASHBOARD_QUERIES } from "~/shared/api/dashboard/dashboard.queries";
import { useUserProfile } from "~/shared/api/users/users.queries";
import {
  Command,
  CommandDialog,
  CommandGroup,
  CommandGroupHeading,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/shared/components/ds/Command";
import { Kbd } from "~/shared/components/ds/Kbd";
import { MODULE_ICON } from "~/shared/constants/module-icon.constants";
import { useDebounce } from "~/shared/hooks/useDebounce";
import { isFromInput } from "~/shared/util/isFromInput.helpers";
import { getNavItems } from "~/shared/util/navigation.helpers";
import { JOB_PATHS, PARTICIPANT_PATHS, PARTNER_PATHS } from "~/team/constants/paths.constants";

export function CommandMenu() {
  const { data: user } = useUserProfile();
  const [open, setOpen] = useCommandMenu();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState("");
  const debouncedSearchTerm = useDebounce(search, 500);
  const params = React.useMemo(() => {
    return {
      ...(debouncedSearchTerm.length > 0 && { term: debouncedSearchTerm }),
    };
  }, [debouncedSearchTerm]);
  const { data, isPending } = useQuery({
    ...DASHBOARD_QUERIES.search({ params }),
    throwOnError: false,
    enabled: debouncedSearchTerm.length > 0,
    staleTime: 20 * 1000, // 20 seconds
  });
  const { jobs, partners, users } = data ?? {};

  const navigationItems = React.useMemo(() => {
    if (!user) return [];

    const navItems =
      user?.type === "breakline"
        ? getNavItems()["breakline"]({ isAdmin: user.roles.includes("admin") })
        : user?.type === "participant"
          ? getNavItems()["participant"]()
          : [];

    return fuzzy.filter<{ label: string; to: string }>(search, navItems, {
      extract: (i) => i.label,
    });
  }, [search, user]);

  /** Focuses the input when the user presses cmd/ctrl+k */
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      /** Enables the user to open the command menu by pressing cmd/ctrl+k or "/" on their keyboard. */
      if (((e.metaKey || e.ctrlKey) && e.key === "k") || e.key === "/") {
        /** We want to skip opening the command menu if the user is typing in an editable html field. */
        if (isFromInput(e)) {
          return;
        }

        e.preventDefault();
        setOpen((open) => !open);
        setSearch("");
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setOpen]);

  const runCommand = React.useCallback(
    (command: () => unknown) => {
      setOpen(false);
      command();
    },
    [setOpen],
  );

  const noResults = !isPending && !(users?.length || jobs?.length || partners?.length || navigationItems.length);

  return (
    <>
      {/** Mobile Trigger */}
      <button
        className={focusRingStyles({
          className:
            "hover:bg-ds-surface-2 hover:ring-ds-stroke-primary grid h-10 w-10 place-items-center rounded-sm ring-1 ring-transparent transition-all md:hidden",
        })}
        onClick={() => {
          setOpen(true);
        }}
      >
        <Icon aria-hidden className="text-ds-text-primary size-4" symbol="search-line-2" />
        <span className="sr-only">Search...</span>
      </button>
      {/** Desktop Trigger */}
      <button
        onClick={() => {
          setOpen(true);
        }}
        className={focusRingStyles({
          className:
            "bg-ds-surface-2 text-ds-text-primary placeholder:text-ds-text-tertiary hidden h-10 w-[240px] items-center justify-between rounded-lg px-3 text-sm transition-shadow md:flex",
        })}
      >
        <span className="stack-x-2">
          <Icon aria-hidden className="text-ds-text-tertiary size-[18px]" symbol="search-line" />
          <span className="block">Search...</span>
        </span>
        <Kbd className="bg-ds-surface-foundation shrink-0">
          <span>⌘</span>
          <span>K</span>
        </Kbd>
      </button>
      <CommandDialog
        open={open}
        onOpenChange={(open) => {
          setOpen(open);

          if (!open) {
            setSearch("");
          }
        }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            onValueChange={setSearch}
            placeholder="Type a command or search..."
            loading={isPending && debouncedSearchTerm.length > 0}
          />
          <CommandList>
            {noResults && <div className="text-ds-text-primary py-6 text-center text-sm">No results found.</div>}

            {navigationItems.length > 0 && (
              <CommandGroup heading={<CommandGroupHeading>Navigation</CommandGroupHeading>}>
                {navigationItems.map(({ original }) => {
                  return (
                    <CommandItem
                      key={`nav_${original.label}`}
                      value={original.label}
                      onSelect={() => {
                        runCommand(() => {
                          navigate(original.to);
                        });
                      }}
                    >
                      <Icon className="mr-2 size-4" aria-hidden symbol="arrow-right-line" />
                      Go To {original.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {users?.length != null && users.length > 0 && (
              <CommandGroup heading={<CommandGroupHeading>Users</CommandGroupHeading>}>
                {users.map((user) => {
                  return (
                    <CommandItem
                      key={`user_${user.id}`}
                      value={user.id.toString()}
                      onSelect={() => {
                        runCommand(() => {
                          navigate(PARTICIPANT_PATHS.detail(user.id.toString()));
                        });
                      }}
                    >
                      <Icon className="mr-2 size-4" aria-hidden symbol="user-line" />
                      {user.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {jobs?.length != null && jobs.length > 0 && (
              <CommandGroup heading={<CommandGroupHeading>Jobs</CommandGroupHeading>}>
                {jobs.map((job) => {
                  return (
                    <CommandItem
                      key={`job_${job.id}`}
                      value={job.id.toString()}
                      onSelect={() => {
                        runCommand(() => {
                          navigate(JOB_PATHS.detail(job.id.toString()));
                        });
                      }}
                    >
                      <Icon className="mr-2 size-4" aria-hidden symbol={MODULE_ICON.roles} />
                      {job.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {partners?.length != null && partners.length > 0 && (
              <CommandGroup heading={<CommandGroupHeading>Partner</CommandGroupHeading>}>
                {partners.map((partner) => {
                  return (
                    <CommandItem
                      key={`partner_${partner.id}`}
                      value={partner.id.toString()}
                      onSelect={() => {
                        runCommand(() => {
                          navigate(PARTNER_PATHS.detail(partner.id.toString()));
                        });
                      }}
                    >
                      <Icon className="mr-2 size-4" aria-hidden symbol={MODULE_ICON.partners} />
                      {partner.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}

const CommandMenuContext = React.createContext<[boolean, React.Dispatch<React.SetStateAction<boolean>>] | null>(null);

export function CommandMenuProvider({ children }: { children: React.ReactNode }) {
  const state = React.useState(false);

  return <CommandMenuContext.Provider value={state}>{children}</CommandMenuContext.Provider>;
}

export function useCommandMenu() {
  const context = React.useContext(CommandMenuContext);

  if (!context) {
    throw new Error("useCommandMenu must be used within a CommandMenuProvider");
  }

  return context;
}
